<template>
  <section
    class="summary"
    :class="{ '--is-expanded' : data.isExpanded }"
  >
    <transition name="summary__panel-transition">
      <content
        v-if="data.isExpanded"
        class="summary__panel"
      >
        <ul class="summary__breakdown">
          <li class="summary__gloss" v-for="item in composerPrices" :key="item.key">
            <div class="summary__gloss-description">{{ priceKeys[item.key] }} {{ item.qty > 1 ? item.qty + 'x' : ''}}</div>
            <div class="summary__gloss-price">
              <Price>
                <template v-slot:brutto>{{ item.singlePrice }}</template>
                <template v-slot:netto>{{ item.netSinglePrice }}</template>
              </Price>
            </div>
          </li>
        </ul>
      </content>
    </transition>

    <footer ref="footer" class="summary__footer">
      <div class="summary__col --is-quantity">
        <h6 class="summary__headline">Menge</h6>

        <div class="summary__slot">
          <FormInput type="number" min="1" :max="'999999'" placeholder="1" v-model="boxQty" class="summary__quantity-input" ref="quantityInput" />
        </div>
      </div>

      <div class="summary__col --is-shipping">
        <h6 class="summary__headline">Verfügbarkeiten</h6>
        <div class="summary__slot">
          <slot v-if="($store.getters.getSelectedItem.data !== null && $store.getters.getSelectedItem.data.type === 'level_3' && $store.getters.getSelectedItem.data.products.length > 0) || ($store.getters.getSelectedItem.data !== null && $store.getters.getSelectedItem.data.type === 'level_1')">
            <slot v-if="$store.getters.getComposerEnvironment === 'present' && $store.getters.isB2b">
              <SummaryAvailability
                :quantity="boxQty"
                :key="$store.getters.getSelectedItem.getDeliveryTime($store)"
              />
            </slot>

            <slot v-else>
              <slot v-if="$store.getters.getSelectedItem.data === null ||
                    $store.getters.getSelectedItem.maxQty($store) < boxQty ||
                    ($store.getters.getCurrentLevel === 1 && !selectedIsInList()) ||
                    ($store.getters.getCurrentLevel === 3 && !allInList())">
                Nicht lieferbar
              </slot>
              <slot v-else>
                <Availability
                  :stock="false"
                  :time="$store.getters.getSelectedItem.getDeliveryTime($store)" class="summary__availability"
                />
                <Popper>
                  <small class="summary__delayed-shipping-text"><a>Später gewünscht?</a></small>
                  <template v-slot:popover>
                    <slot v-if="boxQty === 1">
                      <b>Sie wünschen eine verzögerte Lieferung?</b><br>Gerne können wir den Versand zu einem späteren Zeitpunkt durchführen. Wählen Sie bei den Liefereinstellungen die Option "Lieferung verzögern" und geben Sie Ihren gewünschten Versandtermin an.
                    </slot>
                    <slot v-else>
                      <b>Sie wünschen eine verzögerte Lieferung?</b><br>Gerne können wir den Versand zu einem späteren Zeitpunkt durchführen. Geben Sie Ihren gewünschten Versandtermin bitte im Feld "Anmerkungen zu Ihrer Bestellung" beim Bezahlvorgang an (unter "Rechnungsdetails").
                    </slot>
                  </template>
                </Popper>
              </slot>
            </slot>
          </slot>
        </div>
      </div>

      <div
        class="summary__col --is-price"
        @click="$parent.handleSummaryDetail()"
      >
        <h6 class="summary__headline"><b>Gesamtpreis</b> <i class="icon icon-angle-down"></i></h6>
        <div class="summary__slot">
          <Price align="left">
            <template v-slot:brutto>{{ composerPrices.filter(e => e.key.startsWith('shipping')).length > 1 || composerPrices.filter(e => e.key.startsWith('external')).length > 1 ? 'Ø ' : ''}}{{ humanPrice(composerPrices.map(e => e.multPriceRaw).reduce((e1, e2) => { return e1 + e2}, 0) / boxQty) }}</template>
            <template v-slot:netto>{{ composerPrices.filter(e => e.key.startsWith('shipping')).length > 1 ? 'Ø ' : ''}}{{ humanPrice(composerPrices.map(e => e.netMultPriceRaw).reduce((e1, e2) => { return e1 + e2}, 0) / boxQty) }}</template>
          </Price>
        </div>
      </div>
    </footer>

    <div class="summary__submit-area">
      <Button
        v-if="$store.getters.getCurrentLevel === 1"
        appearance="primary"
        class="summary__btn-continue"
        @click="$emit('submit')"
        :disabled="$store.getters.getSelectedItem.data === null ||
                    $store.getters.getSelectedItem.maxQty($store) < boxQty ||
                    !selectedIsInList()"
      >
        {{ data.submitText }} <i class="icon icon-angle-right"></i>
      </Button>

      <Button
        v-else-if="$store.getters.getCurrentLevel === 3"
        appearance="primary"
        class="summary__btn-continue"
        @click="$emit('submit')"
        :disabled="$store.getters.getSelectedItem.data === null ||
                    $store.getters.getSelectedItem.data.products.length < 1 || !allInList()"
      >
        {{ data.submitText }} <i class="icon icon-angle-right"></i>
      </Button>

      <slot v-if="$store.getters.isB2b">
        <Button
          v-if="$parent.environment === 'present'"
          @click="scrollTo('contact');"
          appearance="secondary"
          class="summary__btn-contact"
          data-ignore-scroll
        >
          Kontakt aufnehmen
        </Button>

        <Button
          v-else
          href="https://componata.de/kontakt/"
          appearance="secondary"
          class="summary__btn-contact"
        >
          Kontakt aufnehmen
        </Button>
      </slot>
    </div>
  </section>
</template>

<script>
import { logicMixin } from '@/mixins/LogicMixin';
import { UiMixin } from '@/mixins/UiMixin';

import FormInput from '@/components/Common/FormInput';
import Button from '@/components/Common/Button';
import Price from '@/components/Common/Price';
import SummaryAvailability from '@/components/Composer/SummaryAvailability';
import Availability from '@/components/Common/Availability';
import Popper from '@/components/Popper';

export default {
  name: 'Summary',
  mixins: [logicMixin, UiMixin],

  props: ['data'],
  components: {
    FormInput,
    Button,
    Price,
    SummaryAvailability,
    Availability,
    Popper
  },

  data () {
    return {
      priceKeys: {
        item: 'Gewähltes Präsent',
        lvl3Box: 'Hochwertige Präsentbox',
        postcard_text: 'Zusätzliche Zeichen handgeschriebener Text',
        postcard_logo: 'Branding Grußkarte',
        postcard_itself: 'Grußkarte',
        postcard_external: 'Externe Grußkarte',
        postcard_ownmotive: 'Grußkarte eigenes Motiv',
        postcard_schuber: 'Branding Box',
        shipping_de: 'Einzelversand Deutschland',
        shipping_eu: 'Einzelversand EU',
        shipping_ch_uk: 'Einzelversand UK/ Schweiz',
        shipping_other: 'Einzelversand Rest der Welt',
        shipping_bulk: 'Sammelversand',
        shipping_cardboardBoxWrap: 'Einzelversandkartons',
        shipping_mixed_int_de: 'Einzelversand Deutschland',
        shipping_mixed_int_eu: 'Einzelversand EU',
        shipping_mixed_int_other: 'Einzelversand Rest der Welt',
        shipping_mixed_int_bulk: 'Sammelversand',
        shipping_mixed_single: 'Einzelversand Deutschland',
        shipping_mixed_bulk: 'Sammelversand Deutschland',
        shipping_mixed_int_ch_uk: 'Einzelversand UK/ Schweiz'
      }
    };
  },

  mounted () {
    this.data.styleProps.panelOffsetPosition = this.$el.offsetHeight;
  },
};
</script>

<style lang="scss" scoped>
/**
 * Summary
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Composer's summary for totals and shipping breakdown info
 * @since 2021-08-18
**/

$component: 'summary';

.#{$component} {
  position: relative;
  width: 100%;
  @include padding(map-get($spacers, 3) map-get($spacers, 4));
  z-index: 6;
  border-top: 1px solid $sky;
  border-bottom-left-radius: $border-radius-lg;
  border-bottom-right-radius: $border-radius-lg;
  background-color: $sky-light;
  box-shadow: inset 0px 4px 5px rgba(0, 0, 0, 0.06);
  color: $ink-light;

  &__panel {
    display: block;
    width: 100%;
    @include margin-bottom(map-get($spacers, 3));
    @include padding-bottom(map-get($spacers, 3));
    border-bottom: 1px solid $gray-ghost;
    transition: all 0.2s ease-in-out;

    * {
      margin: 0;
    }
  }

  @at-root {
    &__panel-transition {
      &-enter-active, &-leave-active {
        opacity: 1;
      }

      &-enter, &-leave-to {
        opacity: 0;
      }
    }
  }

  &__breakdown {
    @include unlist;
  }

  &__gloss {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    &:not(:last-child) {
      @include margin-bottom(map-get($spacers, 2));
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
  }

  &__col {
    &:not(:last-child) {
      @include margin-right(map-get($spacers, 2));
    }

    &.--is-quantity {
      flex-shrink: 1;
      flex-basis: 16%;
    }

    &.--is-shipping {
      small {
        display: block;
        color: $ink-lighter;
      }
    }

    &.--is-price {
      flex-shrink: 1;
      color: $ink;
      cursor: pointer;

      .#{$component}__headline {
        position: relative;
        @include padding-right($font-size-xl * 1);
        color: $ink;

        .icon {
          position: absolute;
          @include rfs(-0.3rem, bottom);
          color: $ink-lighter;
          @include font-size($font-size-xl * 1.3);
          transition: all 0.2s ease-in-out;
        }
      }

      .#{$component}__slot {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        font-weight: $font-weight-bold;

        small {
          @include font-size($font-size-sm);
          font-weight: $font-weight-light;
        }

        > small {
          color: $ink-lightest;
        }
      }

      @include hover () {
        .#{$component}__headline .icon {
          transform: translateY(2px);
        }
      }
    }
  }

  &__headline {
    margin: 0;
    color: $ink-light;
    // @include font-size($font-size-sm);
    font-family: $font-family-base;
    font-weight: $font-weight-normal;
  }

  &__quantity-input {
    width: 100%;
  }

  &__slot {
    @include margin-top(map-get($spacers, 2));
  }

  &__btn-continue {
    .icon {
      @include margin-left(map-get($spacers, 1));
      transition: transform 0.2s ease-in-out;
    }

    @include hover () {
      .icon {
        // transform: translateX(0.4rem);
      }
    }
  }

  &__submit-area {
    display: flex;
    // flex-basis: 42%;
    // justify-content: space-between;
    @include margin(map-get($spacers, 3) (- map-get($spacers, 4)) (- map-get($spacers, 3)) (- map-get($spacers, 4)));
    @include padding(map-get($spacers, 3) map-get($spacers, 4));
    border-top: 1px solid $sky;

    ::v-deep {
      .btn {
        flex: 1 0 50%;
      }
    }
  }

  &__footnote {
    display: none;
    @include margin(map-get($spacers, 2) 0 0 0);
    color: $text-muted;
    @include font-size($font-size-sm);
    text-align: center;
  }

  &.--is-expanded {
    .#{$component} {
      &__col.--is-price .icon {
        transform: rotate(180deg);
      }
    }
  }

  &__delayed-shipping-text {
    display: block;

    a {
      color: $ink-lighter;
      text-decoration: underline;

      @include hover () {
        color: $ink-light;
      }
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-md')) {
    &__footer {
      justify-content: flex-start;
    }

    &__col:not(:last-child) {
      @include margin-right(map-get($spacers, 3));
    }

    &__headline .icon {
      top: 0;
    }
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-sm')) {
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-xs')) {
    &__footer {
      justify-content: space-between;
    }

    &__col {
      &:not(:last-child) {
        @include margin-right(map-get($spacers, 1));
      }

      &.--is-shipping {
        small {
          display: none;
        }
      }
    }

    &__headline {
      > span {
        display: none;
      }
    }

    &__submit-area {
      flex-direction: column;

      ::v-deep .btn + .btn {
        margin-left: 0;
        @include margin-top(map-get($spacers, 2));
      }
    }

    &__footnote {
      display: none;
    }
  }
}
</style>
